import Swiper from 'swiper';
import {
  Autoplay,
  EffectFade,
  FreeMode,
  Mousewheel,
  Navigation,
  Pagination,
  Scrollbar,
  Thumbs,
} from 'swiper/modules';
import {
  breakpoints,
  capitalizeFirstLetterAndLowerCaseRest,
  initializeTeaserClickHandlers,
  isAEMAuthorMode,
  isCarouselCloneSlide,
  setSlider3Behavior,
  subscribeToResize,
  updateCrouselIconsLabels,
  updateImageParams,
} from '@msc-b2c/shared-utils';
import { setupExpandingTeasers } from '../teaser/versions/expandingTeasers';
import { setupStripeTeasers } from '../teaser/versions/stripeTeaser';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/free-mode';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/virtual';
import 'swiper/css/mousewheel';
import 'swiper/css/thumbs';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-coverflow';

type CarouselState = {
  slidesImages?: ICarouselBackgroundImages[];
  carouselContainer: HTMLElement | null;
  previousClone: HTMLElement | null;
  videoInitialized?: boolean;
};
type SwiperState = {
  originalUpdate: () => void;
};

interface ICarouselBackgroundImages {
  id?: string;
  dataURL?: string;
}

declare global {
  interface Window {
    CQ?: any;
    Granite?: any;
    WebKitMutationObserver?: MutationObserver;
    MozMutationObserver?: MutationObserver;
    CMP?: any;
  }
}

const initializedCloneTeasers = new WeakSet<HTMLElement>();

function collectTeasersFromCloneSlides(slides: HTMLElement[]): HTMLElement[] {
  const teasers: HTMLElement[] = [];

  slides.forEach((slide) => {
    slide.querySelectorAll<HTMLElement>('.teaser').forEach((teaser) => {
      if (initializedCloneTeasers.has(teaser)) return;
      initializedCloneTeasers.add(teaser);
      teasers.push(teaser);
    });
  });

  return teasers;
}

function initializeTeasersOnCloneSlides(slides: HTMLElement[]): void {
  const teasers = collectTeasersFromCloneSlides(slides);
  if (!teasers.length) return;

  initializeTeaserClickHandlers(teasers);

  const expandingTeasers = teasers.filter(
    (teaser) =>
      teaser.classList.contains('cmp-teaser__show-xf') ||
      teaser.classList.contains('cmp-teaser__exp-card-slider')
  );
  if (expandingTeasers.length) {
    setupExpandingTeasers(expandingTeasers);
  }

  const stripeTeasers = teasers.filter(
    (teaser) =>
      teaser.classList.contains('cmp-teaser__stripe') ||
      teaser.classList.contains('cmp-teaser__stripe-right')
  );
  if (stripeTeasers.length) {
    setupStripeTeasers(stripeTeasers);
  }

  const slider3Teasers = teasers.filter((teaser) =>
    teaser.classList.contains('cmp-teaser__slidercard3')
  );
  if (slider3Teasers.length) {
    setSlider3Behavior(slider3Teasers);
  }
}

function getAllCarouselCloneSlides(carousel: Element, swiper?: Swiper): HTMLElement[] {
  const fromCarousel = Array.from(
    carousel.querySelectorAll<HTMLElement>(
      '.swiper-slide.loop-clone, .swiper-slide.manual-slide-clone'
    )
  );

  if (!swiper) return fromCarousel;

  const fromSwiper = Array.from(swiper.slides).filter((slide) =>
    isCarouselCloneSlide(slide)
  ) as HTMLElement[];

  return Array.from(new Set([...fromCarousel, ...fromSwiper]));
}

/**
 * Carousels are initialised lazily, only once they are about to enter the viewport, so that
 * a page holding several of them does not pay for every Swiper instance in a single long task.
 *
 * The state below is module scoped on purpose: initNewCarousel() is invoked more than once
 * (module load plus aisCarousel), and the observers must be created only the first time.
 */
type CarouselInitKind = 'static' | 'injected';

const CAROUSEL_PENDING_ATTR = 'data-carousel-init-pending';
const CAROUSEL_VIEWPORT_MARGIN = '200px 0px';
const CAROUSEL_RESCAN_TIMEOUT_MS = 200;
const CAROUSEL_LAZY_INIT_META = 'enableLazyCarouselInit';

const pendingCarouselInit = new WeakMap<Element, () => void>();
let carouselViewportObserver: IntersectionObserver | null = null;
let carouselDiscoveryObserver: MutationObserver | null = null;
let carouselObserversReady = false;
let carouselRescanScheduled = false;
let carouselMinHeightObserved = false;
let carouselLazyInitEnabled: boolean | null = null;

/**
 * Reads the `enableLazyCarouselInit` context-aware flag from its meta tag.
 *
 * When the flag is off the whole page falls back to {@link initAllCarouselsEagerly}, which is the
 * behaviour that predates viewport-driven initialisation. The meta tag is only rendered when the
 * flag is enabled, so a missing tag disables the feature.
 */
function isLazyCarouselInitEnabled(): boolean {
  if (carouselLazyInitEnabled !== null) return carouselLazyInitEnabled;

  const enabled =
    document.querySelector(`meta[name="${CAROUSEL_LAZY_INIT_META}"]`)?.getAttribute('content') ===
    'true';

  // Caching is deferred until parsing is over: this module runs while the document is still being
  // built, and freezing a value read from an incomplete head would make the flag unrecoverable.
  if (document.readyState !== 'loading') carouselLazyInitEnabled = enabled;

  return enabled;
}

function scheduleIdleTask(task: () => void): void {
  const idleCallback = (
    window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    }
  ).requestIdleCallback;

  if (typeof idleCallback === 'function') {
    idleCallback(task, { timeout: CAROUSEL_RESCAN_TIMEOUT_MS });
  } else {
    window.requestAnimationFrame(task);
  }
}

/**
 * Initialises straight away every carousel inside `root` that is still waiting for the viewport.
 *
 * Needed by containers revealed programmatically (modals, tabs): while hidden they never
 * intersect, so their carousels would stay uninitialised.
 */
export function flushPendingCarousels(root: ParentNode = document): void {
  if (!isLazyCarouselInitEnabled()) return;

  root.querySelectorAll(`[${CAROUSEL_PENDING_ATTR}]`).forEach((carousel) => {
    const init = pendingCarouselInit.get(carousel);
    if (!init) return;

    carouselViewportObserver?.unobserve(carousel);
    pendingCarouselInit.delete(carousel);
    init();
  });
}

/**
 * Event-based entry point to {@link flushPendingCarousels}, for reveal paths living in
 * shared-utils, which cannot import this module without creating a dependency cycle.
 *
 * Dispatch it on the element being revealed:
 * `element.dispatchEvent(new CustomEvent('msc:carousel:flush', { bubbles: true }))`
 */
export const CAROUSEL_FLUSH_EVENT = 'msc:carousel:flush';

document.addEventListener(CAROUSEL_FLUSH_EVENT, (event: Event) => {
  const target = event.target;
  flushPendingCarousels(
    target instanceof Element || target instanceof DocumentFragment ? target : document
  );
});

export function initNewCarousel() {
  document.querySelectorAll('.js-card__title').forEach((element) => {
    let words = element.textContent?.trim()?.split(' ');
    words = words?.map((w) => {
      if (w !== 'MSC') {
        return capitalizeFirstLetterAndLowerCaseRest(w);
      } else {
        return w;
      }
    });
    element.textContent = words?.join(' ') ?? '';
  });
  const selectors = {
    self: '.cmp-editorial-carousel',
    autoplayButton: '.swiper-button-autoplay',
    carouselContainer: '.js-automatic-background',
    slideImage: '.cmp-teaser__image .cmp-image img',
  };

  //give control to the carousel swipers to change both with "space" and "enter"
  function initializeNavigatorKeyboardControls(swiper: Swiper, carousel: Element): void {
    const previousButton = carousel.querySelector<HTMLElement>(
      `.swiper-button-prev--${carousel.id}`
    );

    const nextButton = carousel.querySelector<HTMLElement>(`.swiper-button-next--${carousel.id}`);

    const registerButton = (button: HTMLElement | null, direction: 'previous' | 'next') => {
      if (!button || button.dataset['keyboardNavigationInitialized'] === 'true') {
        return;
      }

      button.dataset['keyboardNavigationInitialized'] = 'true';

      button.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          if (event.key !== 'Enter') return;

          event.preventDefault();
          event.stopImmediatePropagation();

          if (button.classList.contains('swiper-button-disabled')) {
            return;
          }

          if (direction === 'previous') {
            swiper.slidePrev();
          } else {
            swiper.slideNext();
          }
        },
        true
      );
    };

    registerButton(previousButton, 'previous');
    registerButton(nextButton, 'next');
  }

  function showHideNavigators(swiper: Swiper, carousel: Element, isParallax?: boolean) {
    const actions = carousel.querySelector('.swiper-actions');
    const navigators = carousel.querySelector('.navigators');

    // Hide navigators in Parallax mode if it has video
    if (isParallax && carouselHasVideo(swiper)) {
      navigators?.classList.add('hide');
      actions?.classList.add('hide');
      return;
    }

    if (swiper.isLocked) {
      if (navigators?.classList.contains('hide')) return;
      navigators?.classList.add('hide');
      actions?.classList.add('hide-actions');
    } else {
      if (!navigators?.classList.contains('hide')) return;
      navigators?.classList.remove('hide');
      actions?.classList.remove('hide-actions');
    }
  }

  // Check if carousel has video
  function carouselHasVideo(swiper: Swiper): boolean {
    return Array.from(swiper.slides).some(
      (slide) => slide.querySelector('.cmp-media--video') !== null
    );
  }

  function getSlidesPerView(
    perView: number,
    isProgressBar: boolean,
    isOneSlideCentered: boolean,
    editorialCarousel: boolean
  ): number {
    if (editorialCarousel) return perView;
    else if (isProgressBar && perView < 5) return perView + 0.5;
    else if (isOneSlideCentered) return 1.2;
    else if (perView >= 5 && !isProgressBar) return perView + 0.7;
    else if (perView >= 5 && isProgressBar) return perView + 0.6;
    else return perView + 0.3;
  }

  // check if navigators are getting out of view port
  // this is the case where button link has large text and pushes it
  function checkIfNavigatorsOutOfView(carousel: Element) {
    const nextButton = carousel.querySelector('.navigators > .swiper-button-next');
    const buttonLink = carousel.querySelector('.swiper-actions > .button');
    const CTA_TEXT_LENGTH_THRESHOLD_FOR_MOBILE_WRAP = 28;

    // return also if there is no button link
    if (!nextButton || !buttonLink) return;

    const parent = carousel.parentElement;
    if (!parent) return;

    const ctaTextEl = buttonLink.querySelector('.cmp-button__text');
    const ctaLength = ctaTextEl?.textContent?.trim().length ?? 0;

    const isMobile = window.innerWidth <= breakpoints.lg;
    const shouldForceWrapOnMobile =
      isMobile && ctaLength >= CTA_TEXT_LENGTH_THRESHOLD_FOR_MOBILE_WRAP;

    if (shouldForceWrapOnMobile) {
      parent.classList.add('button-line-break');
      return;
    }
  }

  const setHrefForCarousel = (carousel: HTMLElement) => {
    const ctaRefs = carousel.querySelectorAll(
      '.swiper-actions .cmp-button, .swiper-wrapper .cmp-teaser__link'
    );
    ctaRefs.forEach((ctaRef) => {
      const uri = ctaRef.getAttribute('href');
      const specialCharactersRegex = /[^a-zA-Z0-9_-]/;
      if (uri) {
        const uriSegments = uri.split('/');
        const lastSegment = uriSegments[uriSegments.length - 1];

        if (
          uri.startsWith('/content') &&
          !lastSegment.includes('.') &&
          !RegExp(specialCharactersRegex).exec(lastSegment)
        ) {
          ctaRef.setAttribute('href', uri + '.html');
        } else if (
          (uri.startsWith('http://') || uri.startsWith('https://')) &&
          uri.endsWith('.html')
        ) {
          ctaRef.setAttribute('href', uri.replace(/\.html$/, ''));
        }
      }
    });
  };

  function initializeThumbnail(carousel: Element): Swiper {
    let didDrag = false;
    return new Swiper(`.cmp-editorial-${carousel.id}--thumbnail`, {
      modules: [EffectFade],
      cssMode: false,
      speed: 600,
      effect: 'fade',
      initialSlide: 1,
      spaceBetween: 0,
      loop: true,
      nested: true,
      rewind: false,
      allowTouchMove: false,
      grabCursor: false,
      navigation: false,
      scrollbar: false,
      pagination: false,
      mousewheel: false,
      keyboard: false,
      freeMode: false,
      preventClicks: true,
      preventClicksPropagation: true,
      preventInteractionOnTransition: false,
      on: {
        touchStart() {
          didDrag = false;
        },
        sliderMove() {
          didDrag = true;
        },
        touchEnd() {
          setTimeout(() => (didDrag = false), 0);
        },
        click(swiper) {
          if (didDrag) return;
          if (swiper.clickedIndex == null) return;
        },
        transitionStart(swiper) {
          swiper.params.preventClicks = false;
        },
        transitionEnd(swiper) {
          swiper.params.preventClicks = true;
        },
      },
    });
  }

  function disableTabNavigationForHiddenSlides(swiper: Swiper) {
    swiper.slides.forEach((slide, index) => {
      const isActive = index === swiper.activeIndex;

      const teaser =
        slide.querySelector<HTMLElement>('.cmp-teaser__action-link') ??
        slide.querySelector<HTMLElement>('.cmp-teaser');

      if (teaser) {
        teaser.setAttribute('tabindex', isActive ? '0' : '-1');
        teaser.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (!teaser.hasAttribute('role')) {
          teaser.setAttribute('role', 'link');
        }
      }

      const focusables = slide.querySelectorAll<HTMLElement>(
        'a, button, input, textarea, select, [tabindex]'
      );

      focusables.forEach((el) => {
        if (el !== teaser) {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  function focusActiveTeaserCTA(swiper: Swiper, carousel: Element) {
    const isFocused = document.activeElement === carousel;
    if (isFocused) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      const teaser = activeSlide?.querySelector<HTMLElement>('[tabindex="0"]');

      if (teaser) {
        teaser.focus({ preventScroll: true });
      }
    }
  }

  function ensureParallaxSentinels(carousel: Element) {
    const wrapper = carousel.querySelector('.swiper-wrapper');
    if (!wrapper) return;
    if (wrapper.querySelector('.parallax-sentinel')) return;

    const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
    if (slides.length !== 3) return;

    const [first, second, third] = slides as HTMLElement[];

    const cloneFirst = first.cloneNode(true) as HTMLElement;
    const cloneSecond = second.cloneNode(true) as HTMLElement;
    const cloneLast = third.cloneNode(true) as HTMLElement;

    cloneFirst.classList.add('parallax-sentinel');
    cloneSecond.classList.add('parallax-sentinel');
    cloneLast.classList.add('parallax-sentinel');

    // Use a fragment to batch DOM changes
    const frag = document.createDocumentFragment();
    frag.appendChild(cloneFirst);
    frag.appendChild(cloneSecond);
    frag.appendChild(cloneLast);

    wrapper.appendChild(frag);
  }

  function startPauseVideo(swiper: Swiper, carousel: Element, state: CarouselState) {
    if (state.videoInitialized) {
      const parallaxRoot = carousel.closest('.parallax');
      const isInitial = parallaxRoot?.classList.contains('-initial');

      carousel.querySelectorAll('.cmp-media__video, .cmp-media__video video').forEach((node) => {
        const video = node instanceof HTMLVideoElement ? node : node.querySelector('video');
        if (video) {
          const videoSlide = video.closest('.swiper-slide');

          // Always pause if parallax is in -initial state
          if (isInitial && window.innerWidth >= 768) {
            video.pause();
            video.controls = false;
            video.removeAttribute('controls');
            return;
          }

          // Handle based on swiper active index
          if (
            videoSlide &&
            swiper.activeIndex === swiper.slides.indexOf(videoSlide as HTMLElement)
          ) {
            video.play().catch((error) => {
              console.error('Error playing video:', error);
            });
            if (window.innerWidth <= breakpoints.md) setTimeout(() => (video.controls = true), 500);
            else video.controls = true;
          } else {
            video.pause();
            video.controls = false;
            video.removeAttribute('controls');
          }
        }
      });
    }
    state.videoInitialized = true;
  }

  function updateParallaxBullets(swiper: Swiper) {
    const paginationWrapper = swiper.el.querySelector('.swiper-pagination') as HTMLElement;
    if (!paginationWrapper) return;
    let bullets = Array.from(paginationWrapper.querySelectorAll('.swiper-pagination-bullet'));

    if (bullets.length > 3) {
      const first = bullets[0];
      const secondLast = bullets[bullets.length - 2];
      const last = bullets[bullets.length - 1];
      first.remove();
      secondLast.remove();
      last.remove();
      bullets = Array.from(paginationWrapper.querySelectorAll('.swiper-pagination-bullet'));
    }

    if (bullets.length !== 3) return;

    const active = swiper.realIndex % 3;

    bullets.forEach((b, i) => {
      const isActive = i === active;
      b.classList.toggle('swiper-pagination-bullet-active', isActive);
      if (isActive) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }

  function initializeSwiper(
    carousel: Element,
    isHero: boolean,
    isOneSlideCentered: boolean,
    isParallax: boolean,
    isGallery: boolean,
    isModalGallery: boolean,
    isSlider3: boolean,
    hasAutoplay: boolean,
    isProgressBar: boolean,
    slidesPerView: number,
    isExperienceCard: boolean,
    isInGrid: boolean,
    thumbnail?: Swiper,
    isExcursionCarousel?: boolean,
    isLoop = false
  ): Swiper {
    const editorialCarouselCase: boolean =
      isInGrid && !(isOneSlideCentered || isHero || isExperienceCard);
    const perViewByCarouselCase = getSlidesPerView(
      slidesPerView,
      isProgressBar,
      isOneSlideCentered,
      editorialCarouselCase
    );

    // State properties of carousel
    const state: CarouselState = {
      slidesImages: [],
      carouselContainer: null,
      previousClone: null,
      videoInitialized: false,
    };

    const isMobile = window.innerWidth <= breakpoints.md;

    // Check if parallax carousel has video before initialization
    const hasParallaxVideo =
      isParallax &&
      Array.from(carousel.querySelectorAll('.swiper-slide')).some(
        (slide) => slide.querySelector('.cmp-media--video') !== null
      );

    function setBackgroundImage(previousImageId: string, nextImageId: string) {
      if (previousImageId) {
        const previousImage = state.carouselContainer?.querySelector(
          `img[data-id-ref=${previousImageId}]`
        ) as HTMLImageElement;

        if (previousImage)
          previousImage.parentElement
            ?.querySelectorAll('.cmp-container__background-image--active')
            ?.forEach((node) => {
              node.classList.remove('cmp-container__background-image--active');
            });
      }

      const backgroundImage = state.carouselContainer?.querySelector(
        `img[data-id-ref=${nextImageId}]`
      ) as HTMLImageElement;

      if (!state.carouselContainer || !state.slidesImages || !backgroundImage) return;

      backgroundImage.classList.add('cmp-container__background-image--active');
    }

    function handleClonesForPreview(swiper: Swiper) {
      const wrapper = swiper.el.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      const currentSlides = wrapper.querySelectorAll('.swiper-slide:not(.manual-slide-clone)');
      if (currentSlides.length === 3) {
        //set the current original slides to have the data-original-slide-index attribute
        currentSlides.forEach((slide, index) => {
          (slide as HTMLElement).setAttribute('data-original-slide-index', String(index));
        });

        const slideToClone = currentSlides[0];
        let shouldUseFirstSlide = true;

        if (state.previousClone) {
          const previousCloneContent = state.previousClone.innerHTML;
          const newCloneContent = slideToClone.innerHTML;

          if (previousCloneContent === newCloneContent) {
            shouldUseFirstSlide = false;
          }

          if (wrapper.contains(state.previousClone)) {
            wrapper.removeChild(state.previousClone);
          }
          state.previousClone = null;
        }

        const slideToActuallyClone = shouldUseFirstSlide ? currentSlides[0] : currentSlides[1];
        const slideIndex = shouldUseFirstSlide ? 0 : 1;
        const clone = slideToActuallyClone.cloneNode(true) as HTMLElement;
        clone.classList.add('manual-slide-clone');
        clone.setAttribute('data-original-slide-index', String(slideIndex));

        wrapper.appendChild(clone);
        state.previousClone = clone;
        initializeTeasersOnCloneSlides([clone]);

        swiper.update();
        const paginationWrapper = swiper.el.querySelector('.swiper-pagination-bullets');
        const bullets = swiper.el.querySelectorAll('.swiper-pagination-bullet');
        if (bullets.length > 3) {
          paginationWrapper?.removeChild(bullets[3]);
        }
      }
    }

    function handlePrevClonesForPreview(swiper: Swiper) {
      const wrapper = swiper.el.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      if (state.previousClone && wrapper.contains(state.previousClone)) {
        wrapper.removeChild(state.previousClone);
      }

      swiper.update();
    }

    /*
      Reload into a state object all available images from the carousel slides and set them as background images
      on the container element. This is used for the oneSlideCenter carousel option
     */
    function setAvailableTeaserImages(swiper: Swiper) {
      if (!swiper) return;
      const carousel = swiper.el;

      state.carouselContainer = carousel.closest(selectors.carouselContainer);
      if (!state.carouselContainer) return;
      state.carouselContainer.style.transition = 'background-image 0.6s ease-in-out';

      const carouselSlides = swiper.slides;
      const loadPromises = Array.from(carouselSlides).map((slide) => {
        const imageLink = slide.querySelector(selectors.slideImage)?.getAttribute('src');
        if (imageLink) {
          const newImageURL = updateImageParams(imageLink, '600', '1');

          return new Promise<void>((resolve) => {
            const slideId = slide.id;
            const img = new Image();

            img.onload = () => {
              // Append image to container
              img.setAttribute('data-id-ref', slideId);
              img.classList.add('cmp-container__background-image');
              img.setAttribute('loading', 'lazy');
              state.carouselContainer?.append(img);

              resolve();
            };

            img.onerror = (error) => {
              console.error('Image failed to load:', newImageURL, error);
              resolve();
            };

            img.src = newImageURL;
          });
        }

        return Promise.resolve();
      });

      Promise.all(loadPromises).then(() => {
        setBackgroundImage('', swiper.slides[swiper.activeIndex].id);
      });
    }

    const originalSlideCount = carousel.querySelectorAll('.swiper-slide')?.length;
    let slideElementsLength = originalSlideCount;
    const disableParallaxCloningInAuthor = isAEMAuthorMode();
    const useManualParallaxLoop =
      isParallax && slideElementsLength === 3 && !disableParallaxCloningInAuthor;

    if (useManualParallaxLoop) {
      ensureParallaxSentinels(carousel);
    }

    // Swiper 12 loop with centeredSlides needs extra slides. Repeat the full
    // small set so navigation keeps a circular order without visible gaps.
    const minSlidesForLoop = 5;
    if (isOneSlideCentered && slideElementsLength >= 3 && slideElementsLength < minSlidesForLoop) {
      const wrapper = carousel.querySelector('.swiper-wrapper');
      const existingSlides = Array.from(carousel.querySelectorAll('.swiper-slide'));
      existingSlides.forEach((slide, index) => {
        slide.setAttribute('data-original-slide-index', String(index));
      });
      const slidesToAdd = existingSlides.length;
      const loopClones: HTMLElement[] = [];
      for (let i = 0; i < slidesToAdd; i++) {
        const previousSlideIndex = i % existingSlides.length;
        const clone = existingSlides[previousSlideIndex].cloneNode(true) as HTMLElement;
        clone.classList.add('loop-clone');
        clone.setAttribute('data-original-slide-index', String(previousSlideIndex));
        wrapper?.appendChild(clone);
        loopClones.push(clone);
      }
      slideElementsLength += slidesToAdd;
    }

    const swiperInstance = !isExperienceCard
      ? new Swiper(`.cmp-editorial-${carousel.id}`, {
          modules: [
            Navigation,
            Pagination,
            Scrollbar,
            Autoplay,
            FreeMode,
            Mousewheel,
            Thumbs,
            EffectFade,
          ],
          watchSlidesProgress: true,
          cssMode: false,
          //  isOneSlideCentered ? true : false,
          centeredSlides: true,
          speed: 600,
          effect: isHero || isGallery ? 'fade' : 'slide',
          loop: (isOneSlideCentered && slideElementsLength > 2) || isHero || isLoop || isParallax,
          rewind: useManualParallaxLoop
            ? false
            : !(
                isProgressBar ||
                (isOneSlideCentered && slideElementsLength > 2) ||
                isHero ||
                isGallery ||
                isParallax
              ),
          loopPreventsSliding: !isProgressBar,
          preventInteractionOnTransition: !isProgressBar,
          grabCursor: !isHero && !isGallery && !isParallax,
          allowTouchMove: true,
          spaceBetween: isHero || isGallery ? 0 : 16,
          slideToClickedSlide: isSlider3,
          autoHeight: !(isSlider3 || isGallery),
          breakpoints: {
            [breakpoints.xs]: {
              slidesPerView: isExcursionCarousel
                ? 'auto'
                : isHero || isGallery
                ? 1
                : isParallax
                ? 1.3
                : 1.2,
              spaceBetween: isHero || isGallery ? 0 : isModalGallery ? 8 : 16,
              slidesOffsetBefore: editorialCarouselCase && !isModalGallery && !isGallery ? 8 : 0,
              slidesOffsetAfter: editorialCarouselCase && !isModalGallery && !isGallery ? 8 : 0,
              centeredSlides: !editorialCarouselCase,
            },
            [breakpoints.s]: {
              autoplay: false,
              slidesPerView: isExcursionCarousel
                ? 'auto'
                : isHero || isGallery || isModalGallery
                ? 1
                : isParallax
                ? 1.55465
                : isOneSlideCentered
                ? 1.3
                : 1.2,
              spaceBetween: isHero || isGallery ? 0 : isModalGallery ? 8 : 16,
              slidesOffsetBefore: editorialCarouselCase && !isModalGallery && !isGallery ? 16 : 0,
              slidesOffsetAfter: editorialCarouselCase && !isModalGallery && !isGallery ? 16 : 0,
              centeredSlides: !editorialCarouselCase,
            },
            [breakpoints.md]: {
              autoplay: true,
              slidesPerView: isExcursionCarousel
                ? 'auto'
                : isHero || isGallery
                ? 1
                : isModalGallery
                ? 1.05
                : isParallax
                ? 1.55465
                : isOneSlideCentered || isSlider3
                ? 1.5
                : 2.8,
              spaceBetween: isHero || isGallery || (isOneSlideCentered && !isParallax) ? 0 : 16,
              centeredSlides:
                editorialCarouselCase || isModalGallery
                  ? false
                  : isOneSlideCentered || isHero || isGallery || !isSlider3 || isParallax,
              slidesOffsetBefore: isHero || isGallery || isModalGallery || isParallax ? 0 : 16,
              slidesOffsetAfter: isHero || isGallery || isModalGallery || isParallax ? 0 : 16,
            },
            [breakpoints.lg]: {
              slidesPerView:
                isHero || isGallery
                  ? 1
                  : isExcursionCarousel
                  ? 'auto'
                  : isOneSlideCentered
                  ? 1.5
                  : isParallax
                  ? 3
                  : isModalGallery
                  ? 1.05
                  : slidesPerView,
              spaceBetween: isModalGallery ? 8 : isParallax || isHero || isGallery ? 0 : 16,
              centeredSlides: isOneSlideCentered || isHero || isGallery || isParallax,
              slidesOffsetBefore:
                editorialCarouselCase && !isModalGallery
                  ? 32
                  : isHero || isOneSlideCentered || isGallery || isModalGallery || isParallax
                  ? 0
                  : 88,
              slidesOffsetAfter:
                editorialCarouselCase && !isModalGallery
                  ? 32
                  : isHero || isOneSlideCentered || isGallery || isModalGallery || isParallax
                  ? 0
                  : 88,
            },
            [breakpoints.xl]: {
              slidesPerView:
                isHero || isGallery
                  ? 1
                  : isModalGallery
                  ? 1.05
                  : isParallax
                  ? 3
                  : isExcursionCarousel
                  ? 'auto'
                  : perViewByCarouselCase,
              spaceBetween: isParallax || isHero || isGallery ? 0 : 16,
              centeredSlides: isOneSlideCentered || isHero || isGallery || isParallax,
              slidesOffsetBefore:
                isHero ||
                isGallery ||
                isOneSlideCentered ||
                isModalGallery ||
                isParallax ||
                !editorialCarouselCase
                  ? 0
                  : 88,
              slidesOffsetAfter:
                isHero ||
                isGallery ||
                isOneSlideCentered ||
                isModalGallery ||
                isParallax ||
                !editorialCarouselCase
                  ? 0
                  : 88,
            },
            [breakpoints.xxl]: {
              slidesPerView:
                isHero || isGallery
                  ? 1
                  : isModalGallery
                  ? 1.05
                  : isParallax
                  ? slideElementsLength > 2
                    ? 3
                    : 1
                  : isExcursionCarousel
                  ? 'auto'
                  : perViewByCarouselCase,
              spaceBetween: isParallax || isHero || isGallery ? 0 : 16,

              centeredSlides: isOneSlideCentered || isHero || isGallery || isParallax,
              slidesOffsetBefore:
                isHero ||
                isGallery ||
                isOneSlideCentered ||
                isModalGallery ||
                editorialCarouselCase ||
                isParallax
                  ? 0
                  : 88,
              slidesOffsetAfter:
                isHero ||
                isGallery ||
                isOneSlideCentered ||
                isModalGallery ||
                editorialCarouselCase ||
                isParallax
                  ? 0
                  : 88,
            },
          },
          thumbs: isHero ? { swiper: thumbnail } : undefined,
          navigation: {
            nextEl: `.swiper-button-next--${carousel.id}`,
            prevEl: `.swiper-button-prev--${carousel.id}`,
            addIcons: false,
          },
          scrollbar: {
            el: `.swiper-scrollbar--${carousel.id}`,
            draggable: true,
            snapOnRelease: false,
            dragSize: 24,
          },
          pagination: {
            el: `.swiper-pagination--${carousel.id}`,
            type: 'bullets',
            clickable: true,
          },
          on: {
            init(swiper) {
              if (isOneSlideCentered) setAvailableTeaserImages(swiper);
              disableTabNavigationForHiddenSlides(swiper);
              if (!isParallax) {
                initializeTeasersOnCloneSlides(getAllCarouselCloneSlides(carousel, swiper));
              }

              if (isParallax) {
                swiper.slideTo(1, 0);
              }

              // Add ARIA roles and labels to each slide for accessibility
              swiper.slides.forEach((slide, index) => {
                slide.setAttribute('role', 'group');
                slide.setAttribute('aria-roledescription', 'slide');
                slide.setAttribute('aria-label', `Slide ${index + 1} of ${swiper.slides.length}`);
              });

              if (swiper.slides.length === 3 && isOneSlideCentered && !isParallax) {
                handleClonesForPreview(swiper);
              }
              if (useManualParallaxLoop) {
                updateParallaxBullets(swiper);
              }

              // Remove extra pagination bullets added by loop clones
              if (isOneSlideCentered && originalSlideCount < minSlidesForLoop) {
                const paginationEl = swiper.el.querySelector('.swiper-pagination-bullets');
                if (paginationEl) {
                  const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
                  for (let i = bullets.length - 1; i >= originalSlideCount; i--) {
                    bullets[i].remove();
                  }
                }
              }
            },
            slideChange(swiper) {
              disableTabNavigationForHiddenSlides(swiper);
              focusActiveTeaserCTA(swiper, carousel);
              if (isParallax && !carouselHasVideo(swiper)) {
                carousel
                  .querySelectorAll('.swiper-slide')
                  ?.forEach((slide) => slide.setAttribute('tabIndex', '-1'));
                carousel.querySelector('.swiper-slide-active')?.setAttribute('tabIndex', '0');
              }
              if (useManualParallaxLoop) {
                updateParallaxBullets(swiper);
              }
              if (isOneSlideCentered && !isParallax) {
                setBackgroundImage(
                  swiper.slides[swiper.previousIndex].id,
                  swiper.slides[swiper.activeIndex].id
                );
              }

              // Keep pagination in sync when clones are present
              if (isOneSlideCentered && originalSlideCount < minSlidesForLoop) {
                const mappedIndex = swiper.realIndex % originalSlideCount;
                const paginationEl = swiper.el.querySelector('.swiper-pagination-bullets');
                if (paginationEl) {
                  const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
                  bullets.forEach((b, i) => {
                    b.classList.toggle('swiper-pagination-bullet-active', i === mappedIndex);
                  });
                }
              }
            },
            click(swiper) {
              // Check if it's a parallax carousel with video
              if (isParallax && carouselHasVideo(swiper)) {
                return; // Exit early, preventing any slide navigation
              }

              if (!(isOneSlideCentered && slideElementsLength > 2)) return;
              const next =
                swiper.slides[swiper.clickedIndex].classList.contains('swiper-slide-next');
              const prev =
                swiper.slides[swiper.clickedIndex].classList.contains('swiper-slide-prev');
              if (
                isParallax &&
                !carousel.closest('.parallax')?.classList.contains('-scrolled') &&
                window.innerWidth >= 768
              ) {
                return;
              } else {
                if (next) swiper.slideNext();
                if (prev) swiper.slidePrev();
              }
            },
            resize(swiper) {
              if (isParallax) return;
              swiper.update();
            },
            update(swiper) {
              showHideNavigators(swiper, carousel);
            },
            transitionEnd(swiper) {
              if (!isParallax) {
                initializeTeasersOnCloneSlides(getAllCarouselCloneSlides(carousel, swiper));
              }
            },
            slideNextTransitionStart(swiper) {
              if (state.previousClone != null && isOneSlideCentered) {
                handleClonesForPreview(swiper);
              }
              if (isParallax && hasParallaxVideo) {
                startPauseVideo(swiper, carousel, state);
              }
            },
            slidePrevTransitionStart(swiper) {
              if (state.previousClone != null && isOneSlideCentered) {
                handlePrevClonesForPreview(swiper);
              }
              if (isParallax && hasParallaxVideo) {
                startPauseVideo(swiper, carousel, state);
              }
            },
          },
          mousewheel: isProgressBar ? { enabled: true, forceToAxis: true } : { enabled: false },
          keyboard: { enabled: true, onlyInViewport: true },
          freeMode: isProgressBar
            ? {
                enabled: true,
                momentumBounce: false,
                sticky: window.innerWidth < breakpoints.md || isSlider3,
              }
            : false,
          autoplay: hasAutoplay
            ? {
                delay: parseInt(carousel?.getAttribute('data-cmp-delay') ?? '') ?? undefined,
                disableOnInteraction: false,
                pauseOnMouseEnter: !carousel.hasAttribute('data-cmp-autopause-disabled'),
              }
            : false,
        })
      : (() => {
          let isExperienceCardRepositioning = false;
          let experienceSlideRepositionTimer: ReturnType<typeof setTimeout> | null = null;
          const isUsExperience =
            isExperienceCard && carousel.classList.contains('-smooth-expandable-cards');
          return new Swiper(`.cmp-editorial-${carousel.id}`, {
            modules: [
              Navigation,
              Pagination,
              ...(isUsExperience ? [Scrollbar] : []),
              Mousewheel,
              FreeMode,
            ],
            watchSlidesProgress: true,
            cssMode: false,
            grabCursor: isProgressBar,
            centeredSlides: !isExperienceCard,
            effect: 'slide',
            loop: isLoop,
            rewind: false,
            speed: 600,
            allowTouchMove: true,
            spaceBetween: 16,
            slidesPerGroup: 1,
            threshold: 30,
            touchRatio: isMobile || isProgressBar ? 1 : 0,
            observer: true,
            slideToClickedSlide: !isExperienceCard,
            freeMode: isProgressBar
              ? {
                  enabled: true,
                  momentumBounce: false,
                  sticky: false,
                }
              : false,
            mousewheel: isProgressBar
              ? {
                  enabled: true,
                  forceToAxis: true,
                  releaseOnEdges: true,
                }
              : { enabled: false },
            keyboard: {
              enabled: true,
            },
            navigation: {
              nextEl: `.swiper-button-next--${carousel.id}`,
              prevEl: `.swiper-button-prev--${carousel.id}`,
              addIcons: false,
            },
            scrollbar: isUsExperience
              ? {
                  el: `.swiper-scrollbar--${carousel.id}`,
                  draggable: true,
                  snapOnRelease: true,
                  dragSize: 24,
                }
              : false,
            pagination: {
              el: `.swiper-pagination--${carousel.id}`,
              type: 'bullets',
              clickable: true,
            },
            on: {
              init(swiper) {
                disableTabNavigationForHiddenSlides(swiper);
                if (!isParallax) {
                  initializeTeasersOnCloneSlides(getAllCarouselCloneSlides(carousel, swiper));
                }
              },
              slideChange(swiper) {
                if (isUsExperience) return;
                if (window.matchMedia(`(max-width: ${breakpoints.lg}px)`).matches) {
                  if (swiper.activeIndex === 0 || isExperienceCard) {
                    swiper.params.centeredSlides = false;
                  } else {
                    swiper.params.centeredSlides = true;
                  }
                  swiper.update();
                }
              },
              slideChangeTransitionEnd(swiper) {
                swiper.pagination.render();
                swiper.pagination.update();
                disableTabNavigationForHiddenSlides(swiper);
                if (!isParallax) {
                  initializeTeasersOnCloneSlides(getAllCarouselCloneSlides(carousel, swiper));
                }
                if (isExperienceCard && !isProgressBar && window.innerWidth > breakpoints.lg) {
                  swiper.slideTo(swiper.activeIndex, 0, false);
                }
              },
              click(swiper) {
                // Keep free scrolling truly free: no forced snap on click.
                if (isExperienceCard && isProgressBar && swiper.clickedIndex != null) return;
              },
              touchEnd(swiper) {
                // Block touch interactions for parallax with video (experience card check)
                if (isParallax && carouselHasVideo(swiper)) {
                  return; // Exit early, preventing any touch navigation
                }

                if (isExperienceCard && !isProgressBar) {
                  return; // Exit early, preventing any touch navigation
                }

                if (isProgressBar) {
                  return; // Let freeMode manage drag end without forced slide snap.
                }

                const diff = swiper.touches.diff;

                if (Math.abs(diff) > 50) {
                  if (diff > 0) {
                    swiper.slidePrev();
                  } else {
                    swiper.slideNext();
                  }
                }
              },
              update(swiper) {
                showHideNavigators(swiper, carousel);
                if (
                  isExperienceCard &&
                  !isProgressBar &&
                  window.innerWidth > breakpoints.lg &&
                  !isExperienceCardRepositioning
                ) {
                  // While the card width animates, observer/update fires often; immediate slideTo each time
                  // shifts translate and reads as a lateral "slide in". Debounce to after layout settles.
                  if (experienceSlideRepositionTimer) {
                    clearTimeout(experienceSlideRepositionTimer);
                  }
                  experienceSlideRepositionTimer = setTimeout(() => {
                    experienceSlideRepositionTimer = null;
                    if (swiper.destroyed) return;
                    isExperienceCardRepositioning = true;
                    swiper.slideTo(swiper.activeIndex, 0, false);
                    isExperienceCardRepositioning = false;
                  }, 250);
                }
              },
            },
            breakpoints: {
              [breakpoints.xs]: {
                centeredSlides: false,
                slidesOffsetBefore: isUsExperience ? 0 : 8,
                slidesOffsetAfter: isUsExperience ? 0 : 8,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
              [breakpoints.s]: {
                centeredSlides: false,
                slidesOffsetBefore: isUsExperience ? 0 : 16,
                slidesOffsetAfter: isUsExperience ? 0 : 16,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
              [breakpoints.md]: {
                centeredSlides: false,
                slidesOffsetBefore: isUsExperience ? 0 : 16,
                slidesOffsetAfter: isUsExperience ? 0 : 16,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
              [breakpoints.lg]: {
                centeredSlides: false,
                slidesOffsetBefore: isUsExperience ? 0 : 32,
                slidesOffsetAfter: isUsExperience ? 0 : 32,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
              [breakpoints.xl]: {
                centeredSlides: false,
                slidesOffsetBefore: isUsExperience ? 0 : 88,
                slidesOffsetAfter: isUsExperience ? 0 : 88,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
              [breakpoints.xxl]: {
                centeredSlides: false,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
              },
            },
          });
        })();

    initializeNavigatorKeyboardControls(swiperInstance, carousel);

    //  Arrow key listener logic here
    setTimeout(() => {
      swiperInstance?.slides?.forEach((slide) => {
        const teaserLink = slide.querySelector<HTMLElement>('.cmp-teaser__action-link');
        teaserLink?.addEventListener('keydown', (event: KeyboardEvent) => {
          const allSlides = swiperInstance.slides;
          const currentSlide = teaserLink?.closest('.swiper-slide');

          if (!currentSlide) return;

          const currentIndex = Array.from(allSlides).indexOf(currentSlide as HTMLElement);

          if (event.key === 'ArrowRight') {
            const nextIndex = currentIndex + 1;

            if (nextIndex < allSlides.length) {
              swiperInstance.slideTo(nextIndex);
              setTimeout(() => {
                const nextSlide = allSlides[nextIndex];
                const nextTeaser = nextSlide.querySelector<HTMLElement>('.cmp-teaser__action-link');
                nextTeaser?.focus({ preventScroll: true });
              }, 300);
            }

            event.preventDefault();
          } else if (event.key === 'ArrowLeft') {
            const prevIndex = currentIndex - 1;

            if (prevIndex >= 0) {
              swiperInstance.slideTo(prevIndex);
              setTimeout(() => {
                const prevSlide = allSlides[prevIndex];
                const prevTeaser = prevSlide.querySelector<HTMLElement>('.cmp-teaser__action-link');
                prevTeaser?.focus({ preventScroll: true });
              }, 300);
            }

            event.preventDefault();
          }
        });
      });
    }, 0);

    return swiperInstance;
  }

  const swiperState: SwiperState = {
    originalUpdate: () => {},
  };

  function initStaticCarousel(carousel: Element) {
    setHrefForCarousel(carousel as HTMLElement);
    const carouselStyleOptions = carousel?.classList;
    const isHero = carouselStyleOptions?.contains('-hero') ?? false;
    if (isHero) carousel.parentElement?.classList.add('-hero');
    const isOneSlideCentered = carouselStyleOptions?.contains('-onecentered') ?? false;
    const isParallax = carousel.parentElement?.classList.contains('cmp-parallax__content') ?? false;
    const isGallery = carouselStyleOptions?.contains('-gallery') ?? false;
    const isModalGallery = carouselStyleOptions?.contains('-modal-gallery') ?? false;
    const isSlider3 = carouselStyleOptions?.contains('-align-bottom') ?? false;
    const isExperienceCard =
      carousel.querySelector('.swiper-slide')?.querySelector('.cmp-teaser__exp-card-slider') !=
      null;
    if (isExperienceCard) {
      carousel.classList.add('-experience');
    }

    const isExcursionCarousel = carouselStyleOptions?.contains('ais-Hits') ?? false;
    const hasAutoplay = carousel.hasAttribute('data-cmp-autoplay');
    const isProgressBar =
      carousel.hasAttribute('data-cmp-is-progressbar') &&
      carousel.getAttribute('data-cmp-is-progressbar') === 'true';
    const perViewValue = parseInt(carousel?.getAttribute('data-cmp-per-view') ?? '');
    const isLoop = carousel.getAttribute('data-cmp-loop-mode') === 'true';
    const slidesPerView =
      !perViewValue || isNaN(perViewValue) || isOneSlideCentered || isHero ? 0.7 : perViewValue;

    let thumbnailSwiper;
    if (isHero) {
      thumbnailSwiper = initializeThumbnail(carousel);
    }

    const container = carousel.closest('.-grid');
    const isInGrid = !!container?.contains(carousel);

    const swiper = initializeSwiper(
      carousel,
      isHero,
      isOneSlideCentered,
      isParallax,
      isGallery,
      isModalGallery,
      isSlider3,
      hasAutoplay,
      isProgressBar,
      slidesPerView,
      isExperienceCard,
      isInGrid,
      thumbnailSwiper,
      isExcursionCarousel,
      isLoop
    );

    showHideNavigators(swiper, carousel);

    thumbnailSwiper?.on('click', function (thumbnail) {
      if (!thumbnail.params.preventClicks) return;

      swiper.slideNext();
    });

    if (hasAutoplay) {
      carousel.setAttribute('data-is-autoplay-running', `${swiper.autoplay.running}`);
    }

    const toggleAutoplayButton = carousel.querySelector(
      `${selectors.autoplayButton}--${carousel.id}`
    );

    if (toggleAutoplayButton) {
      toggleAutoplayButton.addEventListener('click', function () {
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        } else {
          swiper.autoplay.start();
        }
        carousel.setAttribute('data-is-autoplay-running', `${swiper.autoplay.running}`);
      });
    }

    checkIfNavigatorsOutOfView(carousel);
    const cleanup = subscribeToResize(() => {
      if (!(carousel as HTMLElement).isConnected) {
        cleanup();
        return;
      }
      checkIfNavigatorsOutOfView(carousel);
    });

    const applyFanEffect = (carousel: Element, effectClass: string, limit?: number) => {
      if (window.innerWidth <= breakpoints.lg) {
        carousel.classList.remove('fan-effect');
        return;
      }

      swiper.disable();
      const fanSlides = carousel.querySelectorAll(`.swiper.${effectClass} .swiper-slide`);
      const slidesArray = Array.from(fanSlides);
      const targetSlides = limit ? slidesArray.slice(0, limit) : slidesArray;
      const nonTargetSlides = limit ? slidesArray.slice(limit) : [];

      // Batch calculations to avoid layout thrashing
      const middleIndex = Math.ceil((targetSlides.length + 1) / 2);
      const rotations = targetSlides.map((_, index) => (index + 1 - middleIndex) * 5);
      const slides = (targetSlides as HTMLElement[]).map((el, index) => ({
        top: el.getBoundingClientRect().top,
        translateX: index * (-el.offsetWidth + 16 + 90 + rotations[index]),
      }));

      // Do all transformations in a single loop to minimize reflows
      (targetSlides as HTMLElement[]).forEach((el, index) => {
        el.style.transform = `rotate(${rotations[index]}deg) translate(${slides[index].translateX}px)`;
      });

      // Batch calculation of translateY values to avoid multiple reflows
      const translateYValues = (targetSlides as HTMLElement[]).map(
        (el, index) => slides[index].top - el.getBoundingClientRect().top
      );

      // Do all transformations in a single loop to minimize reflows
      (targetSlides as HTMLElement[]).forEach((el, index) => {
        el.style.transform = `rotate(${rotations[index]}deg) translate(${slides[index].translateX}px, ${translateYValues[index]}px)`;
      });

      nonTargetSlides.forEach((slide) => (slide as HTMLElement).classList.add('hidden-slide'));

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              observer.disconnect();
              nonTargetSlides.forEach((slide) =>
                (slide as HTMLElement).classList.remove('hidden-slide')
              );
              carousel.classList.remove('fan-effect');
              carousel.classList.add('normal-layout');
              (targetSlides as HTMLElement[]).forEach((el) => el.style.removeProperty('transform'));
              swiper.enable();
            }
          });
        },
        { threshold: 0.8 }
      );

      observer.observe(carousel);
    };

    if (
      carousel.hasAttribute('data-fan-animation-enabled') &&
      carousel.getAttribute('data-fan-animation-enabled') === 'true'
    ) {
      applyFanEffect(carousel, 'fan-effect');
    } else if (
      carousel.hasAttribute('data-fan-animation-five-enabled') &&
      carousel.getAttribute('data-fan-animation-five-enabled') === 'true'
    ) {
      applyFanEffect(carousel, 'fan-effect-five', 5);
    }
  }

  function initInjectedCarousel(carousel: Element) {
    const carouselStyleOptions = carousel?.classList;
    const isHero = carouselStyleOptions?.contains('-hero') ?? false;
    if (isHero) carousel.parentElement?.classList.add('-hero');
    const isOneSlideCentered = carouselStyleOptions?.contains('-onecentered') ?? false;
    const isParallax = carousel.parentElement?.classList.contains('cmp-parallax__content') ?? false;
    const isGallery = carouselStyleOptions?.contains('-gallery') ?? false;
    const isModalGallery = carouselStyleOptions?.contains('-modal-gallery') ?? false;
    const isSlider3 = carouselStyleOptions?.contains('-align-bottom') ?? false;
    const isExperienceCard =
      carousel.querySelector('.swiper-slide')?.querySelector('.cmp-teaser__exp-card-slider') !=
      null;
    if (isExperienceCard) {
      carousel.classList.add('-experience');
    }
    const isExcursionCarousel = carouselStyleOptions?.contains('ais-Hits') ?? false;

    const hasAutoplay = carousel.hasAttribute('data-cmp-autoplay');
    const isProgressBar =
      carousel.hasAttribute('data-cmp-is-progressbar') &&
      carousel.getAttribute('data-cmp-is-progressbar') === 'true';
    const isLoop = carousel.getAttribute('data-cmp-loop-mode') === 'true';
    const perViewValue = parseInt(carousel?.getAttribute('data-cmp-per-view') ?? '');
    const slidesPerView =
      !perViewValue || isNaN(perViewValue) || isOneSlideCentered || isHero ? 1 : perViewValue;

    let thumbnailSwiper;
    if (isHero) {
      thumbnailSwiper = initializeThumbnail(carousel);
    }
    const container = carousel.closest('.-grid');
    const isInGrid = !!container?.contains(carousel);

    const swiper = initializeSwiper(
      carousel,
      isHero,
      isOneSlideCentered,
      isParallax,
      isGallery,
      isModalGallery,
      isSlider3,
      hasAutoplay,
      isProgressBar,
      slidesPerView,
      isExperienceCard,
      isInGrid,
      thumbnailSwiper,
      isExcursionCarousel,
      isLoop
    );

    showHideNavigators(swiper, carousel);

    thumbnailSwiper?.on('click', function (thumbnail) {
      if (!thumbnail.params.preventClicks) return;

      swiper.slideNext();
      thumbnail.slideNext();
    });

    const toggleAutoplayButton = carousel.querySelector(
      `${selectors.autoplayButton}--${carousel.id}`
    );

    if (toggleAutoplayButton) {
      toggleAutoplayButton.addEventListener('click', function () {
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        } else {
          swiper.autoplay.start();
        }
        carousel.setAttribute('data-is-autoplay-running', `${swiper.autoplay.running}`);
      });
    }
    if (isExperienceCard) {
      const slide = Array.from(carousel.querySelectorAll('.swiper-slide')).find(
        (s) => !s.classList.contains('swiper-slide-active')
      );
      const teaser = slide?.querySelector('.teaser') as HTMLElement;
      if (window.innerWidth > breakpoints.lg) {
        swiper.updateSize = function () {
          Object.assign(swiper, {
            width: this.width,
            height: this.height,
            size: teaser.offsetWidth || this.width,
          });
        };
      } else {
        swiper.updateSize = swiperState.originalUpdate;
      }
    }
  }

  /**
   * A carousel is skipped when Swiper already owns it. Content-fragment carousels are the
   * exception: their markup can be replaced after the first initialisation, so they are
   * allowed to be set up again.
   */
  function shouldInitCarousel(carousel: Element): boolean {
    return !(
      carousel.classList.contains('swiper-initialized') &&
      !carousel.classList.contains('swiper-content-fragment')
    );
  }

  /** Defers initialisation until the carousel is about to enter the viewport. */
  function observeCarousel(carousel: Element, kind: CarouselInitKind) {
    if (carousel.hasAttribute(CAROUSEL_PENDING_ATTR) || !shouldInitCarousel(carousel)) {
      return;
    }

    carousel.setAttribute(CAROUSEL_PENDING_ATTR, 'true');
    pendingCarouselInit.set(carousel, () => {
      carousel.removeAttribute(CAROUSEL_PENDING_ATTR);
      if (!carousel.isConnected || !shouldInitCarousel(carousel)) return;

      if (kind === 'static') {
        initStaticCarousel(carousel);
      } else {
        initInjectedCarousel(carousel);
      }
    });
    carouselViewportObserver?.observe(carousel);
  }

  function scanCarousels(kind: CarouselInitKind) {
    document
      .querySelectorAll(`${selectors.self}:not([${CAROUSEL_PENDING_ATTR}])`)
      .forEach((carousel) => observeCarousel(carousel, kind));
  }

  function requestCarouselRescan() {
    if (carouselRescanScheduled) return;
    carouselRescanScheduled = true;

    scheduleIdleTask(() => {
      carouselRescanScheduled = false;
      scanCarousels('injected');
    });
  }

  function ensureCarouselObservers() {
    if (carouselObserversReady) return;
    carouselObserversReady = true;

    if (isLazyCarouselInitEnabled()) {
      carouselViewportObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            observer.unobserve(entry.target);
            const init = pendingCarouselInit.get(entry.target);
            pendingCarouselInit.delete(entry.target);
            init?.();
          });
        },
        { rootMargin: CAROUSEL_VIEWPORT_MARGIN }
      );
    }

    // Discovery stays on the whole body subtree: a carousel can be authored or injected anywhere,
    // including into containers that do not exist yet, so there is no narrower node that is
    // provably safe to watch. What used to make this expensive was the callback, which ran a
    // querySelectorAll for every added node; it now only raises a flag, and the actual rescan runs
    // once per idle slot no matter how many mutations arrived.
    carouselDiscoveryObserver = new MutationObserver((mutations) => {
      const hasAddedElement = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE)
      );

      if (hasAddedElement) requestCarouselRescan();
    });

    carouselDiscoveryObserver.observe(document.body, { subtree: true, childList: true });
  }

  /**
   * Pre-existing initialisation path, kept verbatim for when the lazy flag is off.
   *
   * It intentionally reproduces three traits of the original implementation, so that turning the
   * flag off restores production behaviour exactly: a fresh MutationObserver per call, no
   * `swiper-initialized` guard on injected carousels, and a scan limited to the descendants of each
   * added node.
   */
  function initAllCarouselsEagerly() {
    document.querySelectorAll(selectors.self).forEach((carousel) => {
      if (!shouldInitCarousel(carousel)) return;
      initStaticCarousel(carousel);
    });

    const MutationObserver =
      window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        Array.from(mutation.addedNodes).forEach((addedNode) => {
          if (!(addedNode instanceof HTMLElement)) return;

          addedNode
            .querySelectorAll(selectors.self)
            .forEach((carousel) => initInjectedCarousel(carousel));
        });
      });
    });

    const body = document.querySelector('body');
    if (body) observer.observe(body, { subtree: true, childList: true });
  }

  function onDocumentReady() {
    // call the function here to check all carousels labels in page and update them
    updateCrouselIconsLabels();

    if (!isLazyCarouselInitEnabled()) {
      initAllCarouselsEagerly();
      return;
    }

    ensureCarouselObservers();
    scanCarousels('static');
  }

  const carouselEl: HTMLElement | null =
    document.querySelector(`.-carousel-min-height > .swiper-wrapper`) ?? null;

  // The duplicate guard only applies to the lazy path: with the flag off every call installs its
  // own observer, as it did before.
  const skipDuplicateMinHeightObserver = carouselMinHeightObserved && isLazyCarouselInitEnabled();

  if (carouselEl && !skipDuplicateMinHeightObserver) {
    carouselMinHeightObserved = true;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        carouselEl.style.minHeight = height + 'px';
      }
    });

    resizeObserver.observe(carouselEl);
  }

  if (document.readyState !== 'loading') {
    onDocumentReady();
  } else {
    document.addEventListener('DOMContentLoaded', onDocumentReady);
  }
}

initNewCarousel();
